import { createAsyncThunk } from '@reduxjs/toolkit'
import { callErrorMsg } from 'helpers/errorMsg'
import {
  getCampaignUmbrellaDetail,
  getCampaignUmbrellaChild,
  deleteCampaignUmbrellaChild,
  GetCampaignUmbrellaChildResponseType,
} from 'features/CampaignAndDiscount/services/campaignUmbrella'
import { toastFailed, toastSuccess } from 'utils/toast'
import { CampaignUmbrellaChildStateType } from 'features/CampaignAndDiscount/@types/campaignUmbrellaChild'
import { mapCampaignChildStatusToLabel } from 'features/CampaignAndDiscount/constant/campaignUmbrellaAddEditDetail'
import {
  SLICE_NAME,
  setIsLoading,
  setDetail,
  setGroupedCampaignChilds,
  setQuery,
  setIsDetailEditable,
  setIsDetailDeletable,
} from './slice'
import {
  setOption,
  PromoCriteriaBMSM,
  PromoCriteriaFlashSale,
  PromoCriteriaRafaksi,
} from '../child'

export type GroupedCampaignChildType = {
  count: number
  items: GetCampaignUmbrellaChildResponseType['data']
  duplicates: number
}

export const fetchCampaignUmbrellaDetail = createAsyncThunk(
  `${SLICE_NAME}/fetchCampaignUmbrellaDetail`,
  async ({ brandSubmissionID }: { brandSubmissionID: string }, { dispatch, rejectWithValue }) => {
    dispatch(setIsLoading(true))

    try {
      const { data: dateRes } = await getCampaignUmbrellaDetail({ brandSubmissionID })

      const { error, data } = dateRes
      if (error.status) {
        toastFailed(error.message)
      } else {
        dispatch(setDetail(data))

        const promoCriteria: CampaignUmbrellaChildStateType['option']['criteria'] = []
        if (data.promo_criteria?.bmsm) {
          promoCriteria.push(PromoCriteriaBMSM)
        }
        if (data.promo_criteria?.flash_sale) {
          promoCriteria.push(PromoCriteriaFlashSale)
        }
        if (data.promo_criteria?.single_discount) {
          promoCriteria.push(PromoCriteriaRafaksi)
        }
        dispatch(setOption({ criteria: promoCriteria }))
      }
      dispatch(setIsLoading(false))

      return dateRes
    } catch (error) {
      callErrorMsg(error)
      dispatch(setIsLoading(false))
      return rejectWithValue(error)
    }
  },
)

export const fetchCampaignUmbrellaChild = createAsyncThunk(
  `${SLICE_NAME}/fetchCampaignUmbrellaChild`,
  async (
    { brandSubmissionID }: { brandSubmissionID: string },
    { dispatch, rejectWithValue, getState },
  ) => {
    const {
      campaignUmbrellaDetail: { query },
    } = getState() as StoreStateType
    dispatch(setIsLoading(true))

    try {
      const { data } = await getCampaignUmbrellaChild({
        brandSubmissionID,
        params: {
          page_size: query.page_size,
          cursor: query.cursor,
          search: query.search,
        },
      })

      // Sort campaignChilds by vendor_name alphabetically
      const sortedCampaignChilds = [...data.data].sort((a, b) =>
        a.vendor_name.localeCompare(b.vendor_name),
      )

      const isDetailEditable = sortedCampaignChilds.some(
        (item) => mapCampaignChildStatusToLabel(item.status) !== 'Submitted to CatMan',
      )

      dispatch(setIsDetailEditable(sortedCampaignChilds.length ? isDetailEditable : true))
      dispatch(setIsDetailDeletable(sortedCampaignChilds.length ? isDetailEditable : true))

      // Calculate vendor statistics
      const vendorStats = sortedCampaignChilds.reduce((acc, item) => {
        const vendorName = item.vendor_name

        const vendorStat = acc[vendorName] || { count: 0, items: [], duplicates: 0 }
        const updatedVendorStat = {
          ...vendorStat,
          count: vendorStat.count + 1,
          items: [...vendorStat.items, item],
        }

        return {
          ...acc,
          [vendorName]: updatedVendorStat,
        }
      }, {} as Record<string, GroupedCampaignChildType>)

      const mappedVendorStats = Object.entries(vendorStats).map(([name, stats]) => ({
        vendorName: name,
        count: stats.count,
        duplicates: stats.duplicates,
        items: stats.items.map((item) => ({
          ...item,
          vendor_approval_date: item.vendor_approval_date || '',
          initial_submitted_date: item.initial_submitted_date || '',
          final_approval_date: item.final_approval_date || '',
          uniqueId: window.crypto.randomUUID(), // Add uniqueId to each item
        })),
      }))

      dispatch(
        setQuery({
          next_cursor: data.pagination.next_cursor || '',
          previous_cursor: data.pagination.prev_cursor,
        }),
      )
      if (query.isInitialFetch) {
        dispatch(
          setQuery({
            isQuerySearchable: sortedCampaignChilds.length > 0,
            isInitialFetch: false,
          }),
        )
      }

      dispatch(
        setGroupedCampaignChilds({
          value: mappedVendorStats,
        }),
      )

      dispatch(setIsLoading(false))
      return data
    } catch (error) {
      callErrorMsg(error)
      dispatch(setIsLoading(false))
      return rejectWithValue(error)
    }
  },
)

export const removeCampaignChild = createAsyncThunk(
  `${SLICE_NAME}/removeCampaignChild`,
  async ({ skpId }: { skpId: string }, { dispatch, rejectWithValue }) => {
    dispatch(setIsLoading(true))
    try {
      const { data } = await deleteCampaignUmbrellaChild({ skpId })
      dispatch(setIsLoading(false))
      if (data.error.status) {
        toastFailed(data.error.message)
      }
      toastSuccess(data.message || 'Child campaign deleted successfully')
      return data
    } catch (error) {
      callErrorMsg(error)
      dispatch(setIsLoading(false))
      return rejectWithValue(error)
    }
  },
)
